<template>
    <div v-if="dispatchItem" class="col-sm-12 px-2 pb-2">
        <h3>{{ $t('dispatch.add') }}</h3>
        <div class="col-12 nopads">
            <span class="hint-text">{{ $t('dispatch.hint') }}</span>
        </div>
        <!-- First choose an order -->
        <div class="row">
            <span class="span-title px-2">{{ $t('dispatch.select_order') }}</span>
            <open-order-filter
                :from="from"
                :order="dispatchItem.work_order"
                v-model="this.dispatchItem.work_order"
                @loading="loading++"
                @loadingCompleted="loading--"
                @orderSelected="setOrder"/>
        </div>

        <!-- Then choose own company users -->
        <div class="col-12">
            <span class="span-title">{{ $t('dispatch.select_users') }}</span>
            <multiselect
                v-model="dispatchItem.persons"
                class="multiselect_routa mt-1"
                :deselect-label="$t('common.remove_selection')"
                :select-label="$t('common.select')"
                :close-on-select="false"
                :multiple="true"
                label="first_name"
                :placeholder="$t('trip_list.select_user')"
                track-by="id"
                :searchable="true"
                :internal-search="true"
                :disabled="users.length <= 0"
                :selected-label="$t('common.selected')"
                :options="users"
            >
                <template #singleLabel="props">
                        <span class="option__desc">
                          <span class="option__title">{{ props.option.last_name + ', ' + props.option.first_name }}</span>
                        </span>
                </template>
                <template #option="props">
                    <div class="option__desc">
                        <span class="option__title multiselect_detail_block">{{ props.option.last_name + ', ' + props.option.first_name }}</span>
                    </div>
                </template>
                <template #noResult>{{ $t('common.not_found') }}</template>
                <template #noOptions>{{ $t('common.no_results') }}</template>
            </multiselect>
        </div>
        <!-- Then choose order executors -->
        <div class="col-12">
            <span class="span-title">{{ $t('dispatch.select_contractors') }}</span>
            <multiselect
                v-model="dispatchItem.contractors"
                class="multiselect_routa mt-1"
                :deselect-label="$t('common.remove_selection')"
                :select-label="$t('common.select')"
                :close-on-select="false"
                :multiple="true"
                label="name"
                track-by="id"
                :placeholder="$t('vehicle_editor.missing_company')"
                :searchable="true"
                :internal-search="true"
                :disabled="orderExecutors.length <= 0"
                :selected-label="$t('common.selected')"
                :options="orderExecutors"
            >
                <template #singleLabel="props">
                        <span class="option__desc">
                          <span class="option__title">{{ props.option.name  }}</span>
                        </span>
                </template>
                <template #option="props">
                    <div class="option__desc">
                        <span class="option__title multiselect_detail_block">{{ props.option.name }}</span>
                    </div>
                </template>
                <template #noResult>{{ $t('common.not_found') }}</template>
                <template #noOptions>{{ $t('common.no_results') }}</template>
            </multiselect>
        </div>
        <!-- Add description and deadline -->
        <div class="col-12">
            <span class="span-title">{{ $t('dispatch.deadline') }}</span>
            <date-time-picker
                v-model:date="dispatchItem.deadline"
                format="dd.MM.yyyy HH:mm"
                :enableTimePicker="true"
            />

        </div>
        <div class="col-12">
            <span class="span-title">{{ $t('mvr_inspection.inspection_editor.site') }}</span>
            <textarea v-model="dispatchItem.description" class="form-control form-control-mb" rows="4" :placeholder="$t('dispatch.descr_hint')"></textarea>
        </div>
        <div class="col-sm-12 d-flex justify-content-end py-2">
            <button
                class="btn btn-secondary form-button"
                @click.stop="$emit('close')"
            >
                {{ $t('common.cancel') }}
            </button>
            <button
                class="btn btn-success form-button"
                @click.stop="onSubmit"
                :disabled="loading > 0"
            >
                {{ $t('delivery_note.add') }}
            </button>
        </div>
        <div
            v-if="loading > 0"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {restApi} from "@/components/mixins/RestApiMixin";
import OpenOrderFilter from "@/components/order/OpenOrderFilter";
import DateTimePicker from "@/components/common/DateTimePicker";
import {inject} from "vue";

export default {
    name: "DispatchEditor",
    components: {OpenOrderFilter, DateTimePicker},
    mixins: [restApi],
    props: {
        item: {
            type: Object,
            required: true
        },
    },
    data() {
        return {
            from: new Date().toISOString(),
            dispatchItem: null,
            users: [],
            orderExecutors: [],
            loading: 0,
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    watch: {
        'this.dispatchItem.work_order' : function () {
            this.setOrder()
        }
    },
    mounted() {
        this.fetchUsers()
    },
    created() {
        this.dispatchItem = this.item
    },
    methods: {

        fetchUsers: function () {
            this.loading++
            this.users = []
            this.restFetchParams(this.userCompanyUrl, {active: 1}, this.handleUserResponse)
        },

        handleUserResponse: function (response) {
            this.loading--
            if (response && response.data) {
                this.users = response.data
            }
        },

        setOrder(order) {
            this.dispatchItem.contractors = []
            this.dispatchItem.work_order = order
            if (order) {
                this.fetchOrderDetails(this.dispatchItem.work_order.id)
            }
        },

        fetchOrderDetails(orderId) {
            this.loading++
            this.orderExecutors = []
            this.restFetch(this.orderUrl + '/' + orderId, this.handleOrder)
        },
        handleOrder(response) {
            this.loading--
            if (response && response.data) {
                const order = response.data
                this.orderExecutors = order.executors ? order.executors.map(item => item.company) : []
            }
        },
        onReset: function () {
            this.$emit('close')
        },
        onSubmit: function () {
            let data = this.validateInput(this.dispatchItem)
            if (data !== undefined) {
                this.loading++
                this.restAdd(this.dispatchUrl, data, this.addSuccess, this.fail)
            }
        },
        validateInput: function (note) {
            if (!note.work_order) {
                this.showAlert(this.$t('dispatch.order_missing'))
                return undefined
            }
            if ((!note.persons || note.persons.length < 1) && (!note.contractors || note.contractors.length < 1)) {
                this.showAlert(this.$t('dispatch.persons_or_contractor_missing'))
                return undefined
            }
            if (!note.deadline) {
                this.showAlert(this.$t('dispatch.deadline_missing'))
                return undefined
            }
            return note
        },
        addSuccess: function () {
            this.loading--
            this.showAlert(this.$t('common.alert_add_success'), 'success')
            // Keep form info (except vehicle and amount) for following notes
            this.$emit('dispatchAdded')
        },
        fail: function () {
            this.showAlert(this.$t('common.alert_add_failed'))
            this.loading--
        }
    }
}
</script>
